import React from 'react'

import { IFocusTarget } from './form-focus-target'

/**
 * Cross-platform imperative handle a FormFocusTargetProvider hands back
 * up to whichever host owns the save flow (DocumentEdit, DocumentAdd,
 * document-creation-flow, workflow submission, etc.). Every provider
 * self-registers on mount by reading FormFocusHandleContext and calling
 * the setter with `this`; on unmount it calls the setter with null.
 *
 * The handle is the minimum surface a save-site needs: after the save comes
 * back with validation errors, call focusFirstError() (no scope) so the
 * provider walks its own registry, jumps the wizard to the section that
 * owns the first errored path in doc order, and focuses / pulses the field.
 */
export interface IFormFocusHandle {
  focusFirstError(scope?: { sectionId?: string }): Promise<string | undefined>
  focusPath(path: string): Promise<boolean>
  getOrderedPaths(): string[]
  getTarget(path: string): IFocusTarget | undefined
}

export type FormFocusHandleSetter = (handle: IFormFocusHandle | null) => void

/**
 * Save-sites publish a setter (usually `(h) => { ref.current = h }`) here.
 * When a FormFocusTargetProvider mounts anywhere inside, it captures its
 * live handle into the ref. Undefined means no host wants a handle; the
 * provider then just serves its descendants without publishing upward.
 */
export const FormFocusHandleContext = React.createContext<FormFocusHandleSetter | undefined>(undefined)
